import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';

import colors from '../utils/colors';

const DESCRIPTION = 'Kartikeya Sharma works on differential privacy, threshold cryptography, and verifiable computation. Sole-authored research on DP-SGD under class imbalance; six peer-reviewed publications on threshold proxy re-encryption.';

const HomeBody = styled.div`
  max-width: 700px;
  margin: 0 auto 70px auto;
`;

const WritingBox = styled.section`
  margin-top: 56px;
  padding: 32px 36px 28px 36px;
  border: 1px solid ${colors.shade1()};
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 3px 14px rgba(0, 0, 0, 0.05);

  @media only screen and (max-width: 32em) {
    padding: 24px 20px 20px 20px;
    margin-top: 40px;
  }
`;

const BlockHeader = styled.h2`
  margin: 0 0 20px 0;
  text-transform: uppercase;
  color: ${colors.base3()};
  font-size: 13px;
  letter-spacing: 2.5px;
  font-weight: normal;
  font-family: 'Josefin Sans', sans-serif;
`;

const PostRow = styled.div`
  padding: 18px 0;
  border-top: 1px solid ${colors.shade1()};

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }
`;

const PostTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 17px;
  font-weight: normal;

  a {
    color: ${colors.base1()};
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: ${colors.accent1()};
    }
  }
`;

const PostSummary = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 24px;
  color: ${colors.base2()};
  font-family: 'Open Sans', sans-serif;
`;

const MoreLink = styled.div`
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid ${colors.shade1()};
  text-align: right;
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;

  a {
    color: ${colors.accent1()};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      color: ${colors.accent2()};
    }
  }
`;

const selected = [
  {
    path: '/blog/smote-cancels-differential-privacy',
    title: 'How SMOTE Quietly Cancels Your Differential Privacy',
    summary: 'Oversampling before private training puts each record and its synthetic copies in one correlated group, so a record-level guarantee silently becomes a group-level one.',
  },
  {
    path: '/blog/building-a-dp-mechanism',
    title: 'What It Takes To Build A Differentially Private Mechanism',
    summary: 'Sensitivity, composition, and the accounting that has to hold end to end before a privacy budget means anything.',
  },
  {
    path: '/blog/securenn-ring-choice',
    title: 'Protocol Review of SecureNN',
    summary: 'A review of eprint 2018/442 checked against both published revisions and the reference implementation, including a routing typo in the 4-party protocol that hands one server the plaintext.',
  },
];

const HomePage = () => (
  <HomeBody>
    <Helmet>
      <title>Kartikeya Sharma</title>
      <meta name="description" content={DESCRIPTION} />
      <meta property="og:description" content={DESCRIPTION} />
      <meta name="twitter:description" content={DESCRIPTION} />
    </Helmet>
    <WritingBox>
      <BlockHeader>Selected writing</BlockHeader>
      {selected.map((post) => (
        <PostRow key={post.path}>
          <PostTitle>
            <Link to={post.path}>{post.title}</Link>
          </PostTitle>
          <PostSummary>{post.summary}</PostSummary>
        </PostRow>
      ))}
      <MoreLink>
        <Link to="/writing">All writing &rarr;</Link>
      </MoreLink>
    </WritingBox>
  </HomeBody>
);

export default HomePage;
